import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { EmojiButton, EmojiSelection } from "@joeattardi/emoji-button";
import { finalize, take, takeUntil } from "rxjs";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { CurrentUserService, getCurrentUserId } from "@gtmhub/users";
import { IdMap } from "@gtmhub/util";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { AssigneesFacade } from "@webapp/assignees/services/assignees/assignees-facade.service";
import { Comment } from "@webapp/comments/models/comments.models";
import { AggregatedReaction, Reaction, ReactionDTO, ReactionType } from "@webapp/comments/models/reactions.models";
import { ReactionsFacade } from "@webapp/comments/services/reactions-facade.service";
import { DestroyService } from "@webapp/core/util/services/destroy.service";

export const MAX_REACTIONS_ALLOWED = 23;

@Component({
  selector: "reactions",
  templateUrl: "./reactions.component.html",
  styleUrls: ["./reactions.component.less"],
  providers: [DestroyService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReactionsComponent implements OnInit, OnDestroy {
  @ViewChild("emojiPickerTrigger")
  public emojiPickerTrigger: ElementRef<HTMLDivElement>;

  @Input() public target: Comment;
  @Input() public targetType: string;

  @Input() public reactions: AggregatedReaction[];
  @Output() public readonly reactionsChange: EventEmitter<AggregatedReaction[]> = new EventEmitter();

  public get isCurrentUserViewOnly(): boolean {
    return this.currentUserService.userHasRole("view-only");
  }

  public assignees: IdMap<Assignee> = {};

  private disableReactUpdate = false;
  private maxReactionsAllowed = MAX_REACTIONS_ALLOWED;

  private emojiButton: EmojiButton;

  public constructor(
    private currentUserService: CurrentUserService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private assigneesFacade: AssigneesFacade,
    private changeDetectorRef: ChangeDetectorRef,
    private reactionsFacade: ReactionsFacade,
    private destroy$: DestroyService
  ) {}

  public ngOnInit(): void {
    this.assignees = this.assigneesFacade.getAssigneesIdMap();
  }

  public ngOnDestroy(): void {
    this.destroyEmojiButton();
  }

  public toggleEmojiPicker(): void {
    const shouldOpenNewInstance = !this.emojiButton || !this.emojiButton.isPickerVisible();

    // clean-up everytime before opening a new instance - instances for other buttons could otherwise interfere
    this.destroyEmojiButton();

    if (!shouldOpenNewInstance) {
      return;
    }

    this.emojiButton = new EmojiButton({
      position: "bottom-start",
      showVariants: false,
      showAnimation: false,
    });

    this.emojiButton.togglePicker(this.emojiPickerTrigger.nativeElement);

    this.emojiButton.on("emoji", (emojiSelection: EmojiSelection) => {
      // emoji name should be with "-" not with " ".
      const selectedReaction: ReactionType = {
        name: emojiSelection.name.replace(/\s+/g, "-").toLowerCase(),
        emoji: emojiSelection.emoji,
      };

      this.reactUpdate(selectedReaction);
      this.destroyEmojiButton();
    });
  }

  public reactUpdate(selectedReaction: ReactionType): void {
    // prevent new updates until the request for the previous one has completed
    if (this.disableReactUpdate || this.isCurrentUserViewOnly) {
      return;
    }

    this.disableReactUpdate = true;

    const existingReaction = this.reactions?.find((reaction) => reaction.name.toLowerCase() === selectedReaction.name);

    if (existingReaction?.currentUserReacted) {
      this.removeReaction(existingReaction);
    } else {
      this.addReaction(selectedReaction);
    }
  }

  private addReaction(selectedReaction: ReactionType): void {
    const isReactionTypeNew = !this.reactions?.some((reaction) => reaction.name === selectedReaction.name);
    const isReactionTypesLimitReached = this.reactions?.length >= this.maxReactionsAllowed;

    if (isReactionTypeNew && isReactionTypesLimitReached) {
      this.uiErrorHandlingService.handleModal({
        title: localize("max_number_of_reactions_reached_title"),
        message: localize("max_number_of_reactions_reached_description", { maxReactionsAllowed: this.maxReactionsAllowed }),
      } as IUIError);

      this.disableReactUpdate = false;

      return;
    }

    const reaction: ReactionDTO = {
      name: selectedReaction.name,
      emoji: selectedReaction.emoji,
      targetId: this.target.id,
      targetType: this.targetType,
    };

    this.reactionsFacade
      .createReaction$(reaction, { isReactionOnGif: !!this.target.gif?.id })
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.disableReactUpdate = false;
          this.changeDetectorRef.markForCheck();
        })
      )
      .subscribe({
        next: (newReaction) => {
          this.updateAggregatedReactionsOnAdd(newReaction);
        },
        error: (error) => {
          this.uiErrorHandlingService.handleModal(error);
        },
      });
  }

  private removeReaction(reaction: AggregatedReaction): void {
    this.reactionsFacade
      .deleteReaction$(reaction.name, this.target.id)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.disableReactUpdate = false;
          this.changeDetectorRef.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.updateAggregatedReactionsOnRemove(reaction);
        },
        error: (error) => {
          this.uiErrorHandlingService.handleModal(error);
        },
      });
  }

  private updateAggregatedReactionsOnAdd(newReaction: Reaction): void {
    this.reactions = this.reactions || [];
    const existingAggregatedReaction = this.reactions.find((reaction) => reaction.name === newReaction.name);

    if (existingAggregatedReaction) {
      existingAggregatedReaction.currentUserReacted = true;
      existingAggregatedReaction.userIds.push(newReaction.userId);
      existingAggregatedReaction.count = existingAggregatedReaction.userIds.length;
    } else {
      this.reactions.push({
        name: newReaction.name,
        emoji: newReaction.emoji,
        targetId: newReaction.targetId,
        targetType: newReaction.targetType,
        userIds: [newReaction.userId],
        currentUserReacted: true,
        count: 1,
      });
    }

    this.reactionsChange.emit(this.reactions);
  }

  private updateAggregatedReactionsOnRemove(deletedReaction: AggregatedReaction): void {
    this.reactions = this.reactions || [];

    const aggregatedReaction = this.reactions.find((reaction) => reaction.name === deletedReaction.name);

    if (aggregatedReaction) {
      aggregatedReaction.currentUserReacted = false;
      aggregatedReaction.userIds = aggregatedReaction.userIds.filter((userId) => userId !== getCurrentUserId());
      aggregatedReaction.count = aggregatedReaction.userIds.length;

      if (aggregatedReaction.count === 0) {
        this.reactions = this.reactions.filter((currentReaction) => currentReaction.name !== deletedReaction.name);
      }
    }

    this.reactionsChange.emit(this.reactions);
  }

  private destroyEmojiButton(): void {
    if (this.emojiButton) {
      this.emojiButton.off("emoji", () => null);
      this.emojiButton.destroyPicker();
      this.emojiButton = null;
    }
  }
}
